import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { pdf } from '@react-pdf/renderer';
import { ArrowLeft } from 'lucide-react';

import { DELETE_WORKOUT } from '../graphql/mutations';
import { trackEvent, trackPageView } from '../utils/gt';
import { downloadBlob, sanitizeFilename } from '../utils/downloadBlob';

import { WorkoutsListPane } from '../components/workouts/WorkoutsListPane';
import { WorkoutView } from '../components/workouts/WorkoutView';
import { WorkoutStartChooser } from '../components/workouts/WorkoutBuilder';
import { useExerciseLibrary, useWorkoutActions, useWorkoutsData } from '../components/workouts/useWorkouts';
import { WorkoutPDF } from '../components/WorkoutPDF';

/**
 * Meus Treinos (A · Índice, the owner's pick, 29/10): the list of workouts beside the open one. A workout is read
 * first (numbers, the sheet, Start) and edited from there. Desktop always shows one, today's unless another is picked;
 * on phones it opens as its own page. The open workout is in the URL (?w=<id>), so back closes it and a link opens it.
 */
export const Workouts = () => {
  const { t, i18n: { language } } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const pageRef = useRef(null);

  const data = useWorkoutsData();
  const { rows, usable, limit, isPro, isLoading, refetchWorkouts } = data;
  const actions = useWorkoutActions(data);
  const { byId: library } = useExerciseLibrary({ skip: !isDesktop });

  const [deleteWorkoutMutation] = useMutation(DELETE_WORKOUT);
  const [exportingId, setExportingId] = useState(null);

  const openId = searchParams.get('w');
  const openRow = rows.find((row) => row.id === openId) || null;
  // Desktop always shows one: the chosen workout, else today's, else the first
  const shown = openRow || (isDesktop ? rows.find((row) => row.isNext) || rows.find((row) => !row.isLocked) || null : null);

  useEffect(() => {
    trackPageView('workout', `${t('appUrl')}/workouts`, '/workouts');
  }, [t]);

  // Saved elsewhere (the editor, the split review) and sent here: say so once, then drop the flag from the URL
  useEffect(() => {
    const created = searchParams.get('sc');
    const updated = searchParams.get('su');
    if (!created && !updated) return;
    toast.success(t(created ? 'notifications.workoutCreated' : 'notifications.workoutUpdated'), {
      toastId: 'workout-success',
      autoClose: 2000,
    });
    setSearchParams(
      (params) => {
        params.delete('sc');
        params.delete('su');
        return params;
      },
      { replace: true, state: location.state }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A workout id that isn't one of the account's: back to the plain list once the list is known
  useEffect(() => {
    if (openId && !isLoading && data.workouts && !openRow) setSearchParams({}, { replace: true });
  }, [openId, isLoading, data.workouts, openRow, setSearchParams]);

  // Phones: the page under the open workout doesn't scroll, and its name takes the focus (for screen readers)
  const isPhonePage = !isDesktop && !!openRow;
  useEffect(() => {
    if (!isPhonePage) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    pageRef.current?.querySelector('h2')?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isPhonePage]);

  const closeView = () => {
    if (location.state?.fromList) navigate(-1);
    else setSearchParams({}, { replace: true });
  };

  const handleOpen = (event, row) => {
    trackEvent('workouts_open', 'Workouts - Open Workout', 'workouts', true);
    if (row.id === openRow?.id) event.preventDefault();
  };

  const handleExportOne = async (workoutId) => {
    if (!actions.canExport()) return;
    const workout = rows.find((row) => row.id === workoutId);
    trackEvent('workouts_export_one_pdf', 'Workouts - Export Gym Sheet', 'workouts', true);
    setExportingId(workoutId);
    try {
      const { name, description, image, exercises, motivation } = workout;
      // The sheet follows the workout's own motivational-phrases setting, as the editor's does
      const blob = await pdf(
        <WorkoutPDF workoutData={{ name, description, image, exercises, motivation: motivation ?? true }} language={language} />
      ).toBlob();
      downloadBlob(blob, `${sanitizeFilename(name)}.pdf`);
    } finally {
      setExportingId(null);
    }
  };

  const handleStart = (workoutId) => {
    trackEvent('workouts_start', 'Workouts - Start Guided Workout', 'workouts', true);
    // Straight to the guided workout: its opening offers the Alexa too (and sends this page's choice events)
    navigate(`/execute/${workoutId}`, { state: { from: 'workouts' } });
  };

  // Resolves to true when the workout is gone, so the confirm dialog can close
  const handleDelete = async (workoutId) => {
    try {
      await deleteWorkoutMutation({ variables: { workoutId } });
      trackEvent('workouts_delete', 'Workouts - Delete Workout', 'workouts', true);
      await refetchWorkouts();
      // The list, in place: going back in history (as closing does) left for the page before, Início after a workout
      // saved from there
      if (openId === workoutId) setSearchParams({}, { replace: true });
      return true;
    } catch {
      toast.error(t('notifications.workoutDeleteError'), { toastId: 'workout-delete-error' });
      return false;
    }
  };

  // No workouts yet (desktop): the ways to build the first one sit beside the empty list
  const pickStart = (how) => actions.handleNew({ start: how });

  const view = shown && (
    <WorkoutView
      key={shown.id}
      workout={shown}
      library={library}
      isDesktop={isDesktop}
      isExporting={exportingId === shown.id}
      unlockLabel={actions.unlockLabel}
      onStart={handleStart}
      onExport={handleExportOne}
      onDelete={handleDelete}
      onUnlock={actions.handleUnlock}
      onEdit={() => trackEvent('workouts_edit', 'Workouts - Edit Workout', 'workouts', true)}
    />
  );

  return (
    <div className="mt-root mt-root--app mt-tr-page">
      {actions.modals}
      <div className="mt-tr-layout">
        <WorkoutsListPane
          rows={rows}
          count={usable.length}
          limit={limit}
          isPro={isPro}
          isLoading={isLoading}
          isDesktop={isDesktop}
          currentId={shown?.id}
          isExportingAll={actions.isExportingAll}
          unlockLabel={actions.unlockLabel}
          onNew={actions.handleNew}
          onExportAll={actions.handleExportAll}
          onUnlock={actions.handleUnlock}
          onOpen={handleOpen}
        />
        {isDesktop &&
          (view ? (
            <section className={`mt-tr-pane mt-tr-detail${shown.isLocked ? ' mt-tr-detail--short' : ''}`} aria-label={t('pages.Workouts.view.paneLabel')}>
              {view}
            </section>
          ) : (
            !isLoading && (
              <section className="mt-tr-pane mt-tr-detail mt-tr-detail--short" aria-label={t('pages.Workouts.view.paneLabel')}>
                <WorkoutStartChooser isPro={isPro} count={rows.length} limit={limit} letter="A" onPick={pickStart} />
              </section>
            )
          ))}
      </div>
      {isPhonePage && (
        <div className="mt-tr-phone-page" ref={pageRef}>
          <div className="mt-tr-bar">
            <button type="button" className="mt-tr-bar__back" onClick={closeView} aria-label={t('pages.Workout.editor.back')}>
              <ArrowLeft aria-hidden="true" />
            </button>
          </div>
          {view}
        </div>
      )}
    </div>
  );
};
